/**
 * SupplierMergeReview — the human review surface for dictionary hygiene.
 *
 * The detector flags pairs of entries that look like the SAME company split
 * across two rows (near-duplicate names, shared/near domains) — the class the
 * exact-collision gate cannot catch. Each pair is (optionally) pre-judged by
 * confidence-gated web research; here a reviewer ratifies it three ways: merge
 * either direction, or keep both. A merge folds one entry into the other in the
 * GLOBAL dictionary and re-aggregates affected runs — a curation action, not a
 * per-report edit. Self-hides when the queue is empty.
 */
import { useCallback, useEffect, useState } from 'react';
import type {
  ResolveSupplierMergeInput,
  SupplierMergeCandidate,
  SupplierMergeDirection,
} from '@aurora/shared';
import { api, ApiClientError } from '../api/client';

export function SupplierMergeReview(): JSX.Element | null {
  const [merges, setMerges] = useState<SupplierMergeCandidate[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [scanning, setScanning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async (): Promise<void> => {
    try {
      const r = await api.listSupplierMerges();
      setMerges(r.merges);
    } catch {
      /* non-fatal — the section just stays hidden */
    } finally {
      setLoading(false);
    }
  }, []);
  useEffect(() => {
    void load();
  }, [load]);

  const resolve = async (
    id: string,
    action: ResolveSupplierMergeInput['action'],
    direction?: SupplierMergeDirection,
  ): Promise<void> => {
    setBusyId(id);
    setError(null);
    setNotice(null);
    try {
      const r = await api.resolveSupplierMerge(id, { action, direction });
      setNotice(r.message);
      setMerges((prev) => prev.filter((m) => m.id !== id));
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Failed to resolve');
    } finally {
      setBusyId(null);
    }
  };

  const rescan = async (): Promise<void> => {
    setScanning(true);
    setError(null);
    setNotice(null);
    try {
      const r = await api.scanSupplierMerges();
      setNotice(
        `Scan complete — ${r.newCandidates} new, ${r.totalPending} pending review.`,
      );
      await load();
    } catch (e) {
      setError(e instanceof ApiClientError ? e.message : 'Scan failed');
    } finally {
      setScanning(false);
    }
  };

  // Hide entirely until we know there's something to review.
  if (loading || merges.length === 0) return null;

  return (
    <details className="card add-supplier-panel" open>
      <summary>Suggested cleanups ({merges.length})</summary>
      <p className="muted">
        Pairs that look like the <strong>same company</strong> split across two
        rows (near-duplicate names or shared websites). Merging folds one into the
        other in the shared dictionary for <strong>all</strong> reports and
        re-aggregates the runs it affects. If they’re genuinely different
        companies, choose <em>Keep both</em> and the pair won’t be flagged again.
      </p>

      {error && <div className="form-error">{error}</div>}
      {notice && <div className="form-notice">{notice}</div>}

      <ul className="feedback-history">
        {merges.map((m) => {
          const busy = busyId === m.id;
          return (
            <li key={m.id}>
              <div>
                <strong>{m.entryAName}</strong>{' '}
                <span className="pill pill-complete">{m.aType}</span>
                {m.aDomains.length > 0 && (
                  <em className="muted"> — {m.aDomains.join(', ')}</em>
                )}
              </div>
              <div>
                <strong>{m.entryBName}</strong>{' '}
                <span className="pill pill-complete">{m.bType}</span>
                {m.bDomains.length > 0 && (
                  <em className="muted"> — {m.bDomains.join(', ')}</em>
                )}
              </div>
              <div className="muted">
                matched by {m.similarity.replace(/_/g, ' ')}
                {m.research && (
                  <>
                    {' · '}research: {m.research.verdict.replace(/_/g, ' ')} (
                    {m.research.confidence}%){' '}
                    {m.research.reasoning && `— ${m.research.reasoning}`}
                    {m.research.sources[0] && (
                      <>
                        {' '}
                        <a
                          href={m.research.sources[0]}
                          target="_blank"
                          rel="noreferrer"
                        >
                          source
                        </a>
                      </>
                    )}
                  </>
                )}
              </div>
              <div className="head-actions" style={{ marginTop: '0.35rem' }}>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => void resolve(m.id, 'merge', 'a_into_b')}
                >
                  Merge “{m.entryAName}” → “{m.entryBName}”
                </button>
                <button
                  type="button"
                  className="btn"
                  disabled={busy}
                  onClick={() => void resolve(m.id, 'merge', 'b_into_a')}
                >
                  Merge “{m.entryBName}” → “{m.entryAName}”
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  disabled={busy}
                  onClick={() => void resolve(m.id, 'keep_separate')}
                >
                  Keep both
                </button>
              </div>
            </li>
          );
        })}
      </ul>

      <div className="head-actions" style={{ marginTop: '0.5rem' }}>
        <button
          type="button"
          className="btn"
          onClick={() => void rescan()}
          disabled={scanning}
        >
          {scanning ? 'Scanning…' : 'Rescan dictionary'}
        </button>
      </div>
    </details>
  );
}
